import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import RingGroupsService from 'services/ServiceRINGGROUPS';
import * as yup from 'yup';
import debounce from 'lodash/debounce';

/**
 * @typedef {Object} RingGroup
 * @property {number} id
 * @property {string} name
 * @property {string} strategy
 * @property {boolean} is_personalized
 * @property {boolean} disable
 * @property {Object} [sector]
 * @property {number} [sector.id]
 * @property {string} [sector.name]
 * @property {string} [sector.description]
 */

/**
 * Hook para buscar grupos de toque com opções de paginação e filtragem. Por padrão buscará apenas grupos de toque que o usuário logado tem acesso.
 * @param {Object} [props={}]
 * @param {boolean} [props.showDisabled=false] - Indica se deve incluir grupos de toque desabilitados.
 * @param {string} [props.login] - O login do usuário que busca os grupos de toque. Defina como null para buscar de todos os usuários.
 * @param {number} [props.itemsPerPage=10] - Número de itens por página na paginação.
 * @returns
 */
const useRingGroups = (props = {}) => {
  const {
    showDisabled: initialShowDisabled = false,
    itemsPerPage = 10,
    login = localStorage.getItem('login')
  } = props;

  const [state, setState] = useState({
    /** @type {RingGroup[]} */
    response: [],
    isLoading: false,
    isError: false,
    error: null,
    totalRegisters: 0,
    activePage: 1,
    searchText: '',
    showDisabled: initialShowDisabled
  });

  const fetch = useCallback(
    async (
      selectedPage = state.activePage,
      search = state.searchText,
      showDisabledParam = state.showDisabled
    ) => {
      setState((prevState) => ({
        ...prevState,
        isLoading: true,
        isError: false,
        error: null
      }));

      try {
        const response = await RingGroupsService.getAllSeach(
          selectedPage,
          itemsPerPage,
          search,
          showDisabledParam,
          login
        );
        const ringGroupList = response?.data?.data ?? [];
        const quantity = response?.data?.totalRegisters ?? 0;

        setState((prevState) => ({
          ...prevState,
          response: ringGroupList,
          totalRegisters: quantity,
          isLoading: false,
          isError: false,
          error: null,
          activePage: selectedPage,
          searchText: search,
          showDisabled: showDisabledParam
        }));
      } catch (err) {
        console.error({ err });
        toast.error(
          err.response?.data?.message || 'Erro ao carregar grupos de toque',
          {
            autoClose: 3000,
            closeOnClick: true
          }
        );
        setState((prevState) => ({
          ...prevState,
          response: [],
          totalRegisters: 0,
          isLoading: false,
          isError: true,
          error: err
        }));
      }
    },
    [itemsPerPage]
  );

  const handlePageChange = useCallback(
    (page) => {
      fetch(page, state.searchText, state.showDisabled);
    },
    [fetch, state.searchText, state.showDisabled]
  );

  const debouncedFetchSearch = useMemo(
    () =>
      debounce((value, showDisabled) => {
        fetch(1, value, showDisabled);
      }, 300),
    [fetch]
  );

  const handleSearchChange = useCallback(
    (value) => {
      setState((prev) => ({
        ...prev,
        searchText: value
      }));

      debouncedFetchSearch(value, state.showDisabled);
    },
    [fetch, state.showDisabled]
  );

  useEffect(() => {
    return () => {
      debouncedFetchSearch.cancel();
    };
  }, [debouncedFetchSearch]);

  const handleShowDisabledChange = useCallback(
    (value) => {
      fetch(1, state.searchText, value);
    },
    [fetch, state.searchText]
  );

  const toggleDisable = useCallback(
    async (id, disable) => {
      try {
        if (disable) {
          if (
            !window.confirm(
              'Tem certeza que deseja desabilitar o grupo de toque?'
            )
          )
            return;
          await RingGroupsService.disable(id);
          toast.info('Grupo de toque desabilitado com sucesso!', {
            autoClose: 3000,
            closeOnClick: true
          });
        } else {
          await RingGroupsService.enable(id);
          toast.info('Grupo de toque recuperado com sucesso!', {
            autoClose: 3000,
            closeOnClick: true
          });
        }
        fetch(state.activePage, state.searchText, state.showDisabled);
      } catch (err) {
        console.error({ err });
        toast.error(
          err.response?.data?.message || 'Ops, algo errado aconteceu!',
          {
            autoClose: 3000,
            closeOnClick: true
          }
        );
      }
    },
    [fetch, state.activePage, state.searchText, state.showDisabled]
  );

  /**
   * Busca um grupo de toque pelo ID e valida a resposta com o esquema Yup.
   * @param {string} id - O ID do grupo de toque a ser buscado.
   * @returns {Promise<RingGroup|null>} O grupo de toque encontrado ou null em caso de erro.
   */
  const fetchRingGroupById = useCallback(async (id) => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      isError: false,
      error: null
    }));
    try {
      const response = await RingGroupsService.findById(id);
      const validatedResponse = await ringGroupResponseSchema.validate(
        response.data,
        { abortEarly: false }
      );

      if (validatedResponse.status === 'OK' && validatedResponse.data) {
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
          isError: false,
          error: null
        }));
        return validatedResponse.data;
      } else {
        throw new Error(
          validatedResponse.message ||
            'Dados do grupo de toque não encontrados.'
        );
      }
    } catch (err) {
      console.error({ err });
      const errorMessage =
        err.name === 'ValidationError'
          ? err.errors.join(', ')
          : err.response?.data?.message ||
            'Erro ao carregar grupo de toque por ID';

      toast.error(errorMessage, {
        autoClose: 3000,
        closeOnClick: true
      });
      setState((prevState) => ({
        ...prevState,
        isLoading: false,
        isError: true,
        error: err
      }));
      return null;
    }
  }, []);

  const createRingGroup = useCallback(
    async (data) => {
      setState((prevState) => ({
        ...prevState,
        isLoading: true,
        isError: false,
        error: null
      }));
      try {
        const response = await RingGroupsService.create(data);
        toast.success('Grupo de toque criado com sucesso!', {
          autoClose: 3000,
          closeOnClick: true
        });
        fetch();
        return response.data;
      } catch (err) {
        console.error({ err });
        const errorMessage =
          err.name === 'ValidationError'
            ? err.errors.join(', ')
            : err.response?.data?.message || 'Erro ao criar grupo de toque';

        toast.error(errorMessage, {
          autoClose: 3000,
          closeOnClick: true
        });
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
          isError: true,
          error: err
        }));
        throw err;
      }
    },
    [fetch]
  );

  const updateRingGroup = useCallback(
    async (id, data) => {
      setState((prevState) => ({
        ...prevState,
        isLoading: true,
        isError: false,
        error: null
      }));
      try {
        await yup
          .number()
          .integer()
          .required('ID é obrigatório para atualização')
          .validate(id);
        await ringGroupInputSchema.validate(data, {
          abortEarly: false,
          context: { isUpdate: true }
        });

        const response = await RingGroupsService.update(id, data);
        toast.success('Grupo de toque atualizado com sucesso!', {
          autoClose: 3000,
          closeOnClick: true
        });
        fetch();
        return response.data;
      } catch (err) {
        console.error({ err });
        const errorMessage =
          err.name === 'ValidationError'
            ? err.errors.join(', ')
            : err.response?.data?.message || 'Erro ao atualizar grupo de toque';

        toast.error(errorMessage, {
          autoClose: 3000,
          closeOnClick: true
        });
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
          isError: true,
          error: err
        }));
        throw err;
      }
    },
    [fetch]
  );

  const deleteRingGroup = useCallback(
    async (id) => {
      setState((prevState) => ({
        ...prevState,
        isLoading: true,
        isError: false,
        error: null
      }));
      try {
        await yup
          .number()
          .integer()
          .required('ID é obrigatório para deletar')
          .validate(id);

        if (
          !window.confirm('Tem certeza que deseja deletar este grupo de toque?')
        ) {
          setState((prevState) => ({ ...prevState, isLoading: false }));
          return;
        }
        await RingGroupsService.delete(id);
        toast.info('Grupo de toque deletado com sucesso!', {
          autoClose: 3000,
          closeOnClick: true
        });
        fetch();
      } catch (err) {
        console.error({ err });
        const errorMessage =
          err.name === 'ValidationError'
            ? err.errors.join(', ')
            : err.response?.data?.message || 'Erro ao deletar grupo de toque';

        toast.error(errorMessage, {
          autoClose: 3000,
          closeOnClick: true
        });
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
          isError: true,
          error: err
        }));
        throw err;
      }
    },
    [fetch]
  );

  const addExtensionsToRingGroup = useCallback(
    async (id, data) => {
      setState((prevState) => ({
        ...prevState,
        isLoading: true,
        isError: false,
        error: null
      }));
      try {
        await yup
          .number()
          .integer()
          .required('ID do grupo de toque é obrigatório')
          .validate(id);
        await yup
          .array()
          .of(yup.string().required())
          .required('IDs das extensões são obrigatórios')
          .validate(data);

        const response = await RingGroupsService.addExtensions(id, data);
        toast.success('Extensões adicionadas com sucesso!', {
          autoClose: 3000,
          closeOnClick: true
        });
        fetchRingGroupById(id);
        return response.data;
      } catch (err) {
        console.error({ err });
        const errorMessage =
          err.name === 'ValidationError'
            ? err.errors.join(', ')
            : err.response?.data?.message || 'Erro ao adicionar extensões';

        toast.error(errorMessage, {
          autoClose: 3000,
          closeOnClick: true
        });
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
          isError: true,
          error: err
        }));
        throw err;
      }
    },
    [fetchRingGroupById]
  );

  const removeExtensionsFromRingGroup = useCallback(
    async (id, data) => {
      setState((prevState) => ({
        ...prevState,
        isLoading: true,
        isError: false,
        error: null
      }));
      try {
        await yup
          .number()
          .integer()
          .required('ID do grupo de toque é obrigatório')
          .validate(id);
        await yup
          .array()
          .of(yup.string().required())
          .required('IDs das extensões são obrigatórios')
          .validate(data);

        const response = await RingGroupsService.removeExtensions(id, data);
        toast.success('Extensões removidas com sucesso!', {
          autoClose: 3000,
          closeOnClick: true
        });
        fetchRingGroupById(id);
        return response.data;
      } catch (err) {
        console.error({ err });
        const errorMessage =
          err.name === 'ValidationError'
            ? err.errors.join(', ')
            : err.response?.data?.message || 'Erro ao remover extensões';

        toast.error(errorMessage, {
          autoClose: 3000,
          closeOnClick: true
        });
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
          isError: true,
          error: err
        }));
        throw err;
      }
    },
    [fetchRingGroupById]
  );

  const options = useMemo(
    () => state.response.map(({ id, name }) => ({ label: name, value: id })),
    [state.response]
  );

  useEffect(() => {
    // Busca inicial com os valores iniciais do estado
    fetch(state.activePage, state.searchText, state.showDisabled);
    // As dependências deste useEffect devem ser apenas `fetch` para garantir que ele rode apenas uma vez na montagem
    // ou quando `fetch` for recriado.
  }, [fetch]);

  return {
    ...state,
    fetch,
    handlePageChange,
    handleSearchChange,
    handleShowDisabledChange,
    toggleDisable,
    fetchRingGroupById,
    createRingGroup,
    updateRingGroup,
    deleteRingGroup,
    addExtensionsToRingGroup,
    removeExtensionsFromRingGroup,
    options
  };
};

export default useRingGroups;

const extensionSchema = yup.object().shape({
  extension_number: yup.string().required('Número de extensão é obrigatório'),
  identifier: yup.string().nullable(),
  secret: yup.string().required('Secret é obrigatório'),
  call_limit: yup
    .number()
    .integer()
    .min(1, 'Limite de chamadas deve ser no mínimo 1')
    .required('Limite de chamadas é obrigatório'),
  callgroup: yup.string().nullable(),
  pickupgroup: yup.string().nullable(),
  transport: yup
    .string()
    .oneOf(['UDP', 'TCP', 'TLS'], 'Transporte inválido')
    .required('Transporte é obrigatório'),
  allow_monitor_extension: yup
    .boolean()
    .required('Permissão para monitorar extensão é obrigatória'),
  allow_internal_call_recording: yup
    .boolean()
    .required('Permissão para gravação interna é obrigatória'),
  allow_external_call_recording: yup
    .boolean()
    .required('Permissão para gravação externa é obrigatória'),
  type: yup
    .string()
    .oneOf(['PJSIP', 'SIP'], 'Tipo inválido')
    .required('Tipo é obrigatório'),
  nat: yup.boolean().required('NAT é obrigatório'),
  disable: yup.boolean().required('Status de desabilitado é obrigatório'),
  cost_center: yup.number().integer().nullable(),
  context: yup.number().integer().required('Contexto é obrigatório'),
  pjsip_configuration_id: yup.number().integer().nullable(),
  blf: yup.boolean().required('BLF é obrigatório')
});

const ringGroupDataSchema = yup.object().shape({
  id: yup.number().integer().required('ID é obrigatório'),
  name: yup
    .string()
    .required('Nome é obrigatório')
    .matches(
      /^[a-zA-Z0-9]+$/,
      'O nome não pode conter caracteres especiais ou espaços'
    ),
  alter_cost_center: yup
    .boolean()
    .required('Alterar centro de custo é obrigatório'),
  ring_strategy: yup
    .string()
    .oneOf(
      ['FEWESTCALLS', 'LEASTRECENT', 'LINEAR', 'RANDOM', 'RINGALL', 'RRMEMORY'],
      'Estratégia de toque inválida'
    )
    .required('Estratégia de toque é obrigatória'),
  ring_time: yup
    .number()
    .integer()
    .min(1, 'Tempo de toque deve ser no mínimo 1')
    .max(300, 'Tempo de toque é no máximo 300 segundos')
    .required('Tempo de toque é obrigatório'),
  enabled: yup.boolean().required('Status de habilitado é obrigatório'),
  created_at: yup.date().required('Data de criação é obrigatória'),
  updated_at: yup.date().required('Data de atualização é obrigatória'),
  extensions: yup.array().of(extensionSchema),
  cost_center_id: yup.number().integer().nullable(),
  audio_id: yup.number().integer().nullable(),
  music_id: yup.number().integer().nullable(),
  periodic_announce_frequency: yup.number().integer().nullable(),
  periodic_announce_id: yup.number().integer().nullable(),
  is_personalized: yup.boolean().required('Personalizado é obrigatório'),
  schedule_time_id: yup.number().integer().nullable(),
  outside_hours_enabled: yup
    .boolean()
    .required('Transbordo fora do horário é obrigatório'),
  outside_hours_type: yup
    .string()
    .oneOf(
      ['QUEUE', 'EXTENSION', 'HANGUP', 'RING_GROUP', null],
      'Tipo de transbordo fora do horário inválido'
    )
    .nullable(),
  outside_hours_destination_id: yup.string().nullable(),
  overflow_enabled: yup.boolean().required('Overflow habilitado é obrigatório'),
  overflow_type: yup
    .string()
    .oneOf(
      ['QUEUE', 'EXTENSION', 'HANGUP', 'RING_GROUP', null],
      'Tipo de overflow inválido'
    )
    .nullable(),
  overflow_destination_id: yup.string().nullable(),
  timeout_enabled: yup.boolean().required('Timeout habilitado é obrigatório'),
  timeout_type: yup
    .string()
    .oneOf(
      ['QUEUE', 'EXTENSION', 'HANGUP', 'RING_GROUP', null],
      'Tipo de timeout inválido'
    )
    .nullable(),
  timeout_destination_id: yup.string().nullable(),
  timeout_seconds: yup
    .number()
    .integer()
    .min(1, 'Segundos de timeout deve ser no mínimo 1')
    .nullable(),
  wrapuptime: yup
    .number()
    .integer()
    .min(0)
    .required('Tempo entre chamadas é obrigatório'),
  record_calls: yup.boolean().required('Gravar chamadas é obrigatório'),
  announcePosition: yup.boolean().required('Anunciar posição é obrigatório'),
  announceFrequency: yup.number().integer().min(0).nullable()
});

const ringGroupResponseSchema = yup.object().shape({
  status: yup
    .string()
    .oneOf(['OK', 'ERROR'], 'Status inválido')
    .required('Status é obrigatório'),
  message: yup.string().nullable(),
  data: ringGroupDataSchema.nullable(),
  errors: yup.array().of(yup.string()).defined('Erros são obrigatórios')
});

const overflowTypeEnum = ['QUEUE', 'EXTENSION', 'HANGUP', 'RING_GROUP'];
const ringStrategyEnum = [
  'FEWESTCALLS',
  'LEASTRECENT',
  'LINEAR',
  'RANDOM',
  'RINGALL',
  'RRMEMORY'
];

export const ringGroupInputSchema = yup
  .object()
  .shape({
    name: yup
      .string()
      .required('Nome é obrigatório')
      .matches(
        /^[a-zA-Z0-9]+$/,
        'O nome não pode conter caracteres especiais ou espaços'
      ),
    alter_cost_center: yup
      .boolean()
      .required('Alterar centro de custo é obrigatório'),
    ring_strategy: yup
      .string()
      .oneOf(ringStrategyEnum, 'Estratégia de toque inválida')
      .required('Estratégia de toque é obrigatória'),
    ring_time: yup
      .number()
      .integer('Tempo de toque deve ser um número inteiro')
      .min(1, 'Tempo de toque deve ser maior que 0')
      .max(300, 'Tempo de toque é no máximo 300 segundos')
      .required('Tempo de toque é obrigatório'),
    enabled: yup.boolean().required('Status de habilitado é obrigatório'),
    extensionsid: yup
      .array()
      .of(yup.string().required())
      .defined('Ramais de toque são obrigatórios'),
    cost_center_id: yup
      .number()
      .integer('ID do setor deve ser um número inteiro')
      .required('O setor deve ser informado'),
    music_id: yup
      .number()
      .integer('ID da música deve ser um número inteiro')
      .nullable(),
    periodic_announce_frequency: yup
      .number()
      .integer('Frequência do anúncio periódico deve ser um número inteiro')
      .nullable(),
    periodic_announce_id: yup
      .number()
      .integer('ID do áudio do anúncio periódico deve ser um número inteiro')
      .nullable(),
    is_personalized: yup.boolean().required('Personalizado é obrigatório'),
    schedule_time_id: yup
      .number()
      .integer('ID do horário de funcionamento deve ser um número inteiro')
      .required('Horário de funcionamento é obrigatório'),
    outside_hours_enabled: yup
      .boolean()
      .required('Transbordo fora do horário é obrigatório'),
    outside_hours_type: yup
      .string()
      .oneOf(
        [...overflowTypeEnum, null],
        'Tipo de transbordo fora do horário inválido'
      )
      .nullable(),
    outside_hours_destination_id: yup
      .string()
      .nullable()
      .transform((val) => {
        // manter a comparação como ==, pois undefined e 'undefined' devem ser convertidos
        if (val == 'undefined') return null;

        return val;
      }),
    overflow_enabled: yup
      .boolean()
      .required('Transbordo ativado é obrigatório'),
    overflow_type: yup
      .string()
      .oneOf(
        [...overflowTypeEnum, null],
        'Tipo de transbordo por ocupação inválido'
      )
      .nullable(),
    overflow_destination_id: yup
      .string()
      .nullable()
      .transform((val) => {
        // manter a comparação como ==, pois undefined e 'undefined' devem ser convertidos
        if (val == 'undefined') return null;

        return val;
      }),
    timeout_enabled: yup
      .boolean()
      .required('Tempo limite ativado é obrigatório'),
    timeout_type: yup
      .string()
      .oneOf(
        [...overflowTypeEnum, null],
        'Tipo de transbordo por tempo limite inválido'
      )
      .nullable(),
    timeout_destination_id: yup
      .string()
      .nullable()
      .transform((val) => {
        // manter a comparação como ==, pois undefined e 'undefined' devem ser convertidos
        if (val == 'undefined') return null;

        return val;
      }),
    timeout_seconds: yup
      .number()
      .integer('Tempo limite em segundos deve ser um número inteiro')
      .min(1, 'Tempo limite em segundos deve ser no mínimo 1')
      .nullable()
      .when('timeout_enabled', {
        is: true,
        then: (schema) =>
          schema.required('Tempo limite em segundos é obrigatório')
      }),
    wrapuptime: yup
      .number()
      .integer('Tempo entre chamadas deve ser um número inteiro')
      .min(0, 'Tempo entre chamadas deve ser maior ou igual a 0')
      .required('Tempo entre chamadas é obrigatório'),
    announcePosition: yup.boolean().required('Anunciar posição é obrigatório'),
    announceFrequency: yup
      .number()
      .integer('Frequência do anúncio deve ser um número inteiro')
      .min(1, 'Frequência do anúncio deve ser no mínimo 1')
      .nullable(),
    record_calls: yup.boolean().required('Gravar chamadas é obrigatório')
  })
  .required();
